import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import {
  Refresh as RefreshIcon,
  DevicesOther as PeripheralIcon,
  HelpOutline as UnknownIcon,
  Error as UnhealthyIcon,
} from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { opsEfemPeripheralsService } from '../../services/OpsEfemPeripheralsService';
import { PRODUCT_NAMES, getDeviceCategoryLabel } from '@cnapse/shared';
import type {
  EfemPeripheralHealthRow,
  EfemPeripheralHealthSummary,
  OpsDeviceHealth,
} from '@cnapse/shared/schemas.js';
import { EnhancedTable, type TableColumn, type FilterConfig } from '../../components/ui/EnhancedTable';
import { formatDateTime, formatRelativeTime } from '../../utils/dateFormatting';

const HEALTH_COLORS: Record<OpsDeviceHealth, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
  unknown: 'default',
  not_applicable: 'info',
};

function SummaryCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: React.ReactNode }) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ color }}>{value.toLocaleString()}</Typography>
      </CardContent>
    </Card>
  );
}

function readInitialGatewayFilter(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('gatewaySerialNumber') ?? '';
}

export default function EfemPeripheralsDashboard() {
  const [rows, setRows] = useState<EfemPeripheralHealthRow[]>([]);
  const [summary, setSummary] = useState<EfemPeripheralHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [gatewayFilter] = useState(readInitialGatewayFilter);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilters, setTableFilters] = useState<Record<string, string>>({
    tenantId: '',
    peripheralCategory: '',
    overallHealth: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let cursor: string | undefined;
      const allRows: EfemPeripheralHealthRow[] = [];
      let lastSummary: EfemPeripheralHealthSummary | null = null;

      do {
        const result = await opsEfemPeripheralsService.getEfemPeripheralHealthList({ cursor, limit: 200 });
        allRows.push(...result.data.peripherals);
        lastSummary = result.data.summary;
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();
      setRows(allRows);
      setSummary(lastSummary);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to load ${PRODUCT_NAMES.AXON} peripheral health`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, tableFilters, pageSize, gatewayFilter]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const tenantNameById = useMemo(
    () => new Map(tenants.map((t) => [String(t.id), t.name])),
    [tenants],
  );

  const filterOptions: FilterConfig[] = useMemo(
    () => [
      {
        field: 'tenantId',
        label: 'Tenant',
        type: 'select',
        options: tenants.map((t) => ({ label: t.name, value: t.id })),
      },
      {
        field: 'peripheralCategory',
        label: 'Category',
        type: 'select',
        options: [
          { label: getDeviceCategoryLabel('barcode-scanner'), value: 'barcode-scanner' },
          { label: getDeviceCategoryLabel('handheld-barcode-scanner'), value: 'handheld-barcode-scanner' },
          { label: getDeviceCategoryLabel('waferid-camera'), value: 'waferid-camera' },
          { label: `${PRODUCT_NAMES.AXON} adapter`, value: 'efem-adapter' },
        ],
      },
      {
        field: 'overallHealth',
        label: 'Health',
        type: 'select',
        options: [
          { label: 'Healthy', value: 'healthy' },
          { label: 'Degraded', value: 'degraded' },
          { label: 'Unhealthy', value: 'unhealthy' },
          { label: 'Unknown', value: 'unknown' },
          { label: 'N/A', value: 'not_applicable' },
        ],
      },
    ],
    [tenants],
  );

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((r) => {
      if (gatewayFilter && r.gatewaySerialNumber !== gatewayFilter) return false;
      if (tableFilters.tenantId && r.tenantId !== tableFilters.tenantId) return false;
      if (tableFilters.peripheralCategory && r.peripheralCategory !== tableFilters.peripheralCategory) return false;
      if (tableFilters.overallHealth && r.overallHealth !== tableFilters.overallHealth) return false;
      if (!q) return true;
      const hay = `${r.gatewaySerialNumber} ${r.peripheralSerialNumber ?? ''} ${r.roleLabel ?? ''} ${r.tenantId ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, searchTerm, tableFilters, gatewayFilter]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const columns: TableColumn<EfemPeripheralHealthRow>[] = useMemo(
    () => [
      { field: 'gatewaySerialNumber', headerName: PRODUCT_NAMES.AXON, width: 130 },
      {
        field: 'tenantId',
        headerName: 'Tenant',
        width: 120,
        renderCell: (row) => tenantNameById.get(row.tenantId ?? '') ?? row.tenantId ?? '-',
      },
      { field: 'siteId', headerName: 'Site', width: 100, renderCell: (row) => row.siteId ?? '-' },
      {
        field: 'peripheralCategory',
        headerName: 'Category',
        width: 130,
        renderCell: (row) => row.peripheralCategory.replace(/-/g, ' '),
      },
      { field: 'roleLabel', headerName: 'Role', width: 150, renderCell: (row) => row.roleLabel ?? '-' },
      {
        field: 'peripheralSerialNumber',
        headerName: 'Peripheral SN',
        width: 130,
        renderCell: (row) => row.peripheralSerialNumber ?? '-',
      },
      {
        field: 'runtimeStatus',
        headerName: 'Runtime',
        width: 100,
        renderCell: (row) => {
          const detail = row.runtimeDetail?.trim();
          const label = row.runtimeStatus ?? 'unknown';
          return detail ? (
            <Tooltip title={detail}>
              <Typography variant="body2" component="span">{label}</Typography>
            </Tooltip>
          ) : (
            label
          );
        },
      },
      {
        field: 'runtimeLastSeenAt',
        headerName: 'Last probe',
        width: 130,
        renderCell: (row) => {
          if (!row.runtimeLastSeenAt) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const absolute = formatDateTime(row.runtimeLastSeenAt);
          const relative = formatRelativeTime(row.runtimeLastSeenAt);
          return (
            <Tooltip title={absolute ? `Probed at ${absolute}` : 'No probe timestamp'}>
              <Typography variant="body2" component="span">{relative || '—'}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'gatewayOverallHealth',
        headerName: 'GW Health',
        width: 100,
        renderCell: (row) => (
          <Chip size="small" label={row.gatewayOverallHealth} color={HEALTH_COLORS[row.gatewayOverallHealth] ?? 'default'} />
        ),
      },
      {
        field: 'overallHealth',
        headerName: 'Overall',
        width: 110,
        renderCell: (row) => (
          <Tooltip title={row.healthReason ?? row.overallHealth}>
            <Chip size="small" label={row.overallHealth} color={HEALTH_COLORS[row.overallHealth]} />
          </Tooltip>
        ),
      },
    ],
    [tenantNameById],
  );

  return (
    <AppListPage>
      {gatewayFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Filtered to gateway <strong>{gatewayFilter}</strong>
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={2} mb={3}>
          <SummaryCard label="Total rows" value={summary.total} color="primary.main" icon={<PeripheralIcon />} />
          <SummaryCard label="Bound" value={summary.bound} color="info.main" icon={<PeripheralIcon />} />
          <SummaryCard label="Healthy" value={summary.healthy} color="success.main" icon={<PeripheralIcon />} />
          <SummaryCard label="Unknown" value={summary.unknown} color="text.secondary" icon={<UnknownIcon />} />
          <SummaryCard label="Unhealthy" value={summary.unhealthy} color="error.main" icon={<UnhealthyIcon />} />
        </Box>
      )}

      <EnhancedTable
        data={pagedRows}
        totalCount={filteredRows.length}
        columns={columns}
        getRowId={(row) => `${row.gatewaySerialNumber}-${row.peripheralCategory}-${row.roleKey ?? row.peripheralSerialNumber ?? 'adapter'}`}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        searchable
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search gateway, peripheral serial, role..."
        filters={tableFilters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => setTableFilters(filters as Record<string, string>)}
        emptyStateTitle={`No ${PRODUCT_NAMES.AXON} peripherals`}
        emptyStateDescription={`No bound peripherals found for provisioned ${PRODUCT_NAMES.AXON} gateways.`}
      />
    </AppListPage>
  );
}
